import ImportParser from '../parser/Parser';
import ImportValidator from '../validation/ValidationEngine';
import { CARD_DRAFT_TYPES } from './cardTemplates';

const SIMPLE_MAX_ANSWER_TOKENS = 4;

export const COMPOSER_ERROR_REASONS = {
  EMPTY: 'empty',
  INVALID_SYNTAX: 'invalidSyntax',
  PARSE_FAILED: 'parseFailed',
  ONE_CARD_ONLY: 'oneCardOnly',
  SIMPLE_TOO_LONG: 'simpleTooLong',
  TYPE_MISMATCH: 'typeMismatch',
  NOTE_NAME_REQUIRED: 'noteNameRequired',
  CARD_LIMIT: 'cardLimit',
};

export function countAnswerTokens(value) {
  return String(value || '').trim().split(/\s+/).filter(Boolean).length;
}

export function inferDraftType(pair) {
  if (pair?.cardType === 'reading') {
    const hasSpeech = Array.isArray(pair.readingSegments) &&
      pair.readingSegments.some((segment) => segment?.type === 'speech' || segment?.speaker);
    return hasSpeech ? CARD_DRAFT_TYPES.DIALOG : CARD_DRAFT_TYPES.ARTICLE;
  }

  if (pair?.word === true) return CARD_DRAFT_TYPES.SIMPLE;
  if (pair?.word === false) return CARD_DRAFT_TYPES.NORMAL;
  return countAnswerTokens(pair?.answer) <= SIMPLE_MAX_ANSWER_TOKENS
    ? CARD_DRAFT_TYPES.SIMPLE
    : CARD_DRAFT_TYPES.NORMAL;
}

export function toPreviewCard(pair, index = 0) {
  const inferredType = inferDraftType(pair);
  return {
    id: `draft-${index}`,
    front: pair.question,
    back: pair.answer,
    cardType: pair.cardType === 'reading'
      ? 'reading'
      : inferredType === CARD_DRAFT_TYPES.SIMPLE ? 'simple' : 'normal',
    readingTitle: pair.readingTitle,
    readingContent: pair.readingContent,
    readingText: pair.readingText,
    readingSegments: pair.readingSegments,
    readingCharacterNames: pair.readingCharacterNames,
    contextPre: pair.contextPre || null,
    memo: pair.memo || null,
    contextAfter: pair.contextAfter || null,
    backMemo: pair.backMemo || null,
    ttsText: pair.ttsText || null,
    word: pair.word,
    draftType: inferredType,
  };
}

export function validateComposerContent(content, options = {}) {
  const { expectedType = null, requireSingleCard = false } = options;
  if (!String(content || '').trim()) {
    return { isValid: false, error: { reason: COMPOSER_ERROR_REASONS.EMPTY } };
  }

  const validator = new ImportValidator();
  const validation = validator.validate(content);
  if (!validation.isValid) {
    return {
      isValid: false,
      error: {
        reason: COMPOSER_ERROR_REASONS.INVALID_SYNTAX,
        originalMessage: validation.error?.message || '',
        code: validation.error?.code,
        lineNumber: validation.error?.lineNumber,
      },
    };
  }

  let pairs;
  try {
    pairs = new ImportParser().parse(content);
  } catch (error) {
    return {
      isValid: false,
      error: {
        reason: COMPOSER_ERROR_REASONS.PARSE_FAILED,
        originalMessage: error.message || '',
      },
    };
  }

  const invalidReadingPair = pairs.find((pair) => {
    if (pair?.cardType !== 'reading' || !Array.isArray(pair.readingSegments)) return false;
    return !pair.readingSegments.some((segment) => {
      if (segment?.type === 'story') return Boolean(String(segment.text || '').trim());
      if (segment?.type !== 'speech' && !segment?.speaker) return false;
      return String(segment.text || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean).length >= 2;
    });
  });
  if (invalidReadingPair) {
    return {
      isValid: false,
      error: {
        reason: COMPOSER_ERROR_REASONS.INVALID_SYNTAX,
        originalMessage: 'Reading cards need at least one practice segment.',
      },
    };
  }

  if (requireSingleCard && pairs.length !== 1) {
    return {
      isValid: false,
      error: {
        reason: COMPOSER_ERROR_REASONS.ONE_CARD_ONLY,
        count: pairs.length,
      },
    };
  }

  if (expectedType && pairs.length === 1) {
    const actualType = inferDraftType(pairs[0]);
    if (expectedType === CARD_DRAFT_TYPES.SIMPLE && actualType === CARD_DRAFT_TYPES.NORMAL) {
      return {
        isValid: false,
        error: { reason: COMPOSER_ERROR_REASONS.SIMPLE_TOO_LONG },
      };
    }
    if (actualType !== expectedType) {
      return {
        isValid: false,
        error: {
          reason: COMPOSER_ERROR_REASONS.TYPE_MISMATCH,
          expectedType,
          actualType,
        },
      };
    }
  }

  return {
    isValid: true,
    pairs,
    cards: pairs.map(toPreviewCard),
  };
}

export function stripOuterSeparators(value) {
  const lines = String(value || '').replace(/\r\n/g, '\n').split('\n');
  const isSeparator = (line) => /^-{3,}\s*$/.test(line.trim());
  while (lines.length > 0 && !lines[0].trim()) lines.shift();
  while (lines.length > 0 && !lines[lines.length - 1].trim()) lines.pop();
  if (lines.length > 0 && isSeparator(lines[0])) lines.shift();
  if (lines.length > 0 && isSeparator(lines[lines.length - 1])) lines.pop();
  return lines.join('\n').trim();
}

export function appendCardBlock(content, draft) {
  const existing = String(content || '').trimEnd();
  const block = stripOuterSeparators(draft);
  if (!block) return existing;
  if (!existing) return `---\n${block}\n---`;

  const existingLines = existing.split(/\r?\n/);
  const endsWithSeparator = /^-{3,}\s*$/.test(existingLines[existingLines.length - 1].trim());
  return endsWithSeparator
    ? `${existing}\n${block}\n---`
    : `${existing}\n---\n${block}\n---`;
}
